import { DEFAULT_TIMEZONE, type DeliverableStatus } from "@dream/config/constants";
import {
  type CalendarFilters,
  localDateKey,
  localMonthRange,
  localWeekRange,
  parseFocusDate,
  startOfLocalDay,
} from "@dream/domain";
import { ErrorState, Skeleton, useToast } from "@dream/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, unwrap } from "../api/client.ts";
import {
  type CalendarCommand,
  sortGoogleOverlay,
  visibleGoogleOverlay,
  withoutDreamWritebackOverlay,
} from "../calendar/adapter/mapping.ts";
import { DreamCalendar } from "../calendar/dream-calendar.tsx";
import {
  calendarPeriodPhrase,
  calendarSliceTitle,
  formatSliceJumpLabel,
} from "../components/calendar-chrome.ts";
import { CalendarEmptyHint } from "../components/calendar-empty-hint.tsx";
import { CalendarSliceBanner } from "../components/calendar-slice-banner.tsx";
import { CalendarToolbar } from "../components/calendar-toolbar.tsx";
import { DeliverableDialog } from "../components/deliverable-dialog.tsx";
import { EventContextMenu, type EventContextMenuState } from "../components/event-context-menu.tsx";
import { ShareAgendaDialog } from "../components/share-agenda-dialog.tsx";
import { occurrencesToUpcomingItems, UpcomingRail } from "../components/upcoming-rail.tsx";
import { NeedClientDialog, WorkspaceSetupBanner } from "../components/workspace-setup.tsx";
import {
  useAdminCalendar,
  useAdminCatalog,
  useGoogleCalendarEvents,
  useGoogleCalendarStatus,
  useInvalidateCalendar,
  useMoveDeliverable,
  useResizeDeliverable,
} from "../hooks/use-calendar.ts";
import { useRealtime } from "../hooks/use-realtime.ts";
import { useTimelineDays } from "../hooks/use-viewport.ts";
import { pushRecentDeliverable } from "../preferences/preferences.ts";
import { usePreferences } from "../preferences/provider.tsx";
import { calendarRoute } from "../router.tsx";

/**
 * Painel administrativo do calendário.
 *
 * Une navegação, filtros na URL, o adapter do FullCalendar e o tempo real. As
 * mutações passam pelos hooks, que já tratam rollback visual e invalidação de
 * cache.
 */

function parseCsv(value: string | undefined): string[] {
  return value === undefined ? [] : value.split(",").filter((v) => v !== "");
}

function toCsv(values: string[]): string | undefined {
  return values.length === 0 ? undefined : values.join(",");
}

function extraLabel(label: string, count: number): string {
  return count > 1 ? `${label} +${count - 1}` : label;
}

export function CalendarPage() {
  const search = useSearch({ from: calendarRoute.id });
  const view = search.view ?? "mes";
  const groupBy = search.groupBy ?? "none";
  const colorBy = search.colorBy ?? "type";
  const fonte = search.fonte ?? "todas";
  const navigate = useNavigate({ from: calendarRoute.fullPath });
  const toast = useToast();
  const queryClient = useQueryClient();
  const invalidateCalendar = useInvalidateCalendar();
  const { prefs, setPrefs } = usePreferences();

  const timezone = DEFAULT_TIMEZONE;

  const [dialog, setDialog] = useState<
    | { mode: "closed" }
    | { mode: "create"; startsAt?: string; endsAt?: string; allDay?: boolean }
    | { mode: "edit"; deliverableId: string; occurrenceStart: string }
  >(() =>
    search.entrega === undefined
      ? { mode: "closed" }
      : {
          mode: "edit",
          deliverableId: search.entrega,
          occurrenceStart: new Date().toISOString(),
        },
  );
  const [sharing, setSharing] = useState(false);
  const [needClient, setNeedClient] = useState(false);
  const [contextMenu, setContextMenu] = useState<EventContextMenuState | null>(null);
  const [contextVersion, setContextVersion] = useState(1);
  const timelineDays = useTimelineDays();

  const focusDate = useMemo(() => parseFocusDate(search.date, timezone), [search.date]);

  /**
   * Janela consultada.
   *
   * Deliberadamente maior que a exibida: a grade mensal mostra dias das semanas
   * vizinhas, e a timeline cobre a janela visível a partir da data de
   * referência. Buscar só o mês exato deixaria as bordas vazias.
   */
  const range = useMemo(() => {
    if (view === "dia") {
      const start = startOfLocalDay(focusDate, timezone);
      return {
        start: DateTime.fromJSDate(start, { zone: timezone }).minus({ days: 1 }).toJSDate(),
        end: DateTime.fromJSDate(start, { zone: timezone }).plus({ days: 2 }).toJSDate(),
      };
    }
    if (view === "semana") {
      const week = localWeekRange(focusDate, timezone);
      return {
        start: DateTime.fromJSDate(week.start, { zone: timezone }).minus({ days: 1 }).toJSDate(),
        end: DateTime.fromJSDate(week.end, { zone: timezone }).plus({ days: 1 }).toJSDate(),
      };
    }
    if (view === "linha") {
      const start = startOfLocalDay(focusDate, timezone);
      return {
        start,
        end: DateTime.fromJSDate(start, { zone: timezone })
          .plus({ days: timelineDays + 1 })
          .toJSDate(),
      };
    }
    const month = localMonthRange(focusDate, timezone);
    return {
      start: DateTime.fromJSDate(month.start, { zone: timezone }).minus({ days: 7 }).toJSDate(),
      end: DateTime.fromJSDate(month.end, { zone: timezone }).plus({ days: 7 }).toJSDate(),
    };
  }, [focusDate, view, timelineDays]);

  const filters = useMemo<CalendarFilters>(
    () => ({
      clientIds: parseCsv(search.clients),
      projectIds: parseCsv(search.projects),
      typeIds: parseCsv(search.types),
      assigneeIds: parseCsv(search.assignees),
      statuses: parseCsv(search.statuses) as DeliverableStatus[],
      tagIds: [],
      search: search.q ?? null,
    }),
    [search],
  );

  const hasSlice = filters.clientIds.length > 0 || filters.assigneeIds.length > 0;

  const queryParams = useMemo(
    () => ({
      start: range.start.toISOString(),
      end: range.end.toISOString(),
      filters,
    }),
    [range, filters],
  );

  const upcomingParams = useMemo(() => {
    const start = DateTime.now().setZone(timezone).startOf("day");
    return {
      start: start.toUTC().toISO() ?? queryParams.start,
      end: start.plus({ days: 14 }).toUTC().toISO() ?? queryParams.end,
      filters: {
        clientIds: filters.clientIds,
        projectIds: filters.projectIds,
        typeIds: [] as string[],
        assigneeIds: filters.assigneeIds,
        statuses: [] as DeliverableStatus[],
        tagIds: [] as string[],
        search: null,
      },
    };
  }, [
    queryParams.start,
    queryParams.end,
    filters.clientIds,
    filters.projectIds,
    filters.assigneeIds,
  ]);
  const sliceScanParams = useMemo(() => {
    const start = DateTime.fromJSDate(range.start, { zone: timezone });
    return {
      start: start.toUTC().toISO() ?? queryParams.start,
      end: start.plus({ days: 120 }).toUTC().toISO() ?? queryParams.end,
      filters: {
        clientIds: filters.clientIds,
        projectIds: filters.projectIds,
        typeIds: filters.typeIds,
        assigneeIds: filters.assigneeIds,
        statuses: filters.statuses,
        tagIds: [] as string[],
        search: filters.search,
      },
    };
  }, [range.start, queryParams.start, queryParams.end, filters]);
  const calendar = useAdminCalendar(queryParams);
  const upcomingQuery = useAdminCalendar(upcomingParams);
  const sliceScan = useAdminCalendar(sliceScanParams, hasSlice);
  const googleEvents = useGoogleCalendarEvents({
    start: queryParams.start,
    end: queryParams.end,
  });
  const googleStatus = useGoogleCalendarStatus();
  const catalog = useAdminCatalog();
  const me = useQuery({
    queryKey: ["me"],
    queryFn: async () => unwrap(await api.api.me.get()) as { userId: string; email: string },
  });
  const moveMutation = useMoveDeliverable();
  const resizeMutation = useResizeDeliverable();

  const duplicateMutation = useMutation({
    mutationFn: async (deliverableId: string) =>
      unwrap(await api.api.admin.deliverables({ id: deliverableId }).duplicate.post({})),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
      toast.success("Entrega duplicada", "A cópia foi criada como Planejado.");
    },
    onError: (error: unknown) =>
      toast.error(
        "Não foi possível duplicar",
        error instanceof ApiError ? error.message : "Tente novamente.",
      ),
  });

  const deleteFromMenu = useMutation({
    mutationFn: async (input: { id: string; version: number }) =>
      unwrap(
        await api.api.admin
          .deliverables({ id: input.id })
          .delete(undefined, { query: { version: String(input.version) } }),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["calendar"] });
      toast.success("Entrega excluída");
    },
    onError: (error: unknown) =>
      toast.error(
        "Não foi possível excluir",
        error instanceof ApiError ? error.message : "Tente novamente.",
      ),
  });

  const connection = useRealtime({
    onChange: useCallback(
      (message) => {
        invalidateCalendar(message.at);
      },
      [invalidateCalendar],
    ),
    onResync: invalidateCalendar,
  });

  const setSearch = useCallback(
    (patch: Partial<typeof search>) => {
      void navigate({
        search: (prev) => ({ ...prev, ...patch }),
        replace: true,
      });
    },
    [navigate],
  );

  const openCreate = useCallback(() => {
    if (catalog.clients.length === 0) {
      setNeedClient(true);
      return;
    }
    setDialog({ mode: "create" });
  }, [catalog.clients.length]);

  useEffect(() => {
    if (search.nova !== "1") return;
    openCreate();
    setSearch({ nova: undefined });
  }, [openCreate, search.nova, setSearch]);

  const handleNavigate = useCallback(
    (direction: "prev" | "next" | "today") => {
      if (direction === "today") {
        setSearch({ date: undefined });
        return;
      }

      const step = direction === "next" ? 1 : -1;
      const current = DateTime.fromJSDate(focusDate, { zone: timezone });

      const next =
        view === "dia"
          ? current.plus({ days: step })
          : view === "semana"
            ? current.plus({ weeks: step })
            : view === "linha"
              ? current.plus({ days: timelineDays * step })
              : current.plus({ months: step });

      setSearch({ date: localDateKey(next.toJSDate(), timezone) });
    },
    [focusDate, view, setSearch, timelineDays],
  );

  const reportFailure = useCallback(
    (error: unknown, action: string) => {
      if (error instanceof ApiError && error.isVersionConflict) {
        const current = error.details?.currentVersion;
        toast.error(
          "A entrega foi alterada por outra pessoa",
          `Sua alteração não foi salva e o calendário voltou ao estado anterior.${
            typeof current === "number" ? ` Versão atual no servidor: ${current}.` : ""
          }`,
        );
        return;
      }

      toast.error(
        `Não foi possível ${action}`,
        error instanceof ApiError ? error.message : "Verifique a conexão e tente novamente.",
      );
    },
    [toast],
  );

  const handleCommand = useCallback(
    (command: CalendarCommand) => {
      switch (command.kind) {
        case "move":
          moveMutation.mutate(
            {
              deliverableId: command.deliverableId,
              version: command.version,
              startsAt: command.startsAt,
              endsAt: command.endsAt,
              occurrenceStart: command.occurrenceStart,
              isAllDay: command.allDay,
              revert: command.revert,
            },
            {
              onSuccess: () => {
                toast.success(
                  command.occurrenceStart === undefined
                    ? "Entrega reagendada"
                    : "Ocorrência reagendada",
                  command.occurrenceStart === undefined
                    ? undefined
                    : "As outras ocorrências da série não foram alteradas.",
                );
              },
              onError: (error) => reportFailure(error, "reagendar a entrega"),
            },
          );
          break;

        case "resize":
          resizeMutation.mutate(
            {
              deliverableId: command.deliverableId,
              version: command.version,
              endsAt: command.endsAt,
              occurrenceStart: command.occurrenceStart,
              revert: command.revert,
            },
            {
              onSuccess: () => toast.success("Período atualizado"),
              onError: (error) => reportFailure(error, "alterar o período"),
            },
          );
          break;

        case "open":
          setDialog({
            mode: "edit",
            deliverableId: command.deliverableId,
            occurrenceStart: command.occurrenceStart,
          });
          setSearch({ entrega: command.deliverableId });
          pushRecentDeliverable({
            id: command.deliverableId,
            title: command.title ?? "Entrega",
          });
          break;

        case "context":
          setContextMenu({
            deliverableId: command.deliverableId,
            occurrenceStart: command.occurrenceStart,
            title: command.title,
            typeId: command.typeId,
            color: command.color,
            startsAt: command.startsAt,
            endsAt: command.endsAt,
            allDay: command.allDay,
            x: command.x,
            y: command.y,
          });
          setContextVersion(command.version);
          break;

        case "createRange":
          if (catalog.clients.length === 0) {
            setNeedClient(true);
            break;
          }
          setDialog({
            mode: "create",
            startsAt: command.startsAt,
            endsAt: command.endsAt,
            allDay: command.allDay,
          });
          break;
      }
    },
    [moveMutation, resizeMutation, reportFailure, toast, setSearch, catalog.clients.length],
  );

  const occurrences = calendar.data?.occurrences ?? [];
  const overlayAll = sortGoogleOverlay(
    visibleGoogleOverlay(
      withoutDreamWritebackOverlay(googleEvents.data ?? [], occurrences, catalog.clients),
    ),
  );
  const overlay = fonte === "entregas" || hasSlice ? [] : overlayAll;
  const visibleOccurrences = fonte === "agenda" && !hasSlice ? [] : occurrences;
  const upcomingItems = useMemo(
    () =>
      occurrencesToUpcomingItems(
        upcomingQuery.data?.occurrences ?? [],
        catalog.types,
        catalog.clients,
        new Date(),
        8,
        catalog.team,
      ),
    [upcomingQuery.data?.occurrences, catalog.types, catalog.clients, catalog.team],
  );
  const isInitialLoad = calendar.isLoading && calendar.data === undefined;
  const googleConnected = googleStatus.data?.connected === true;
  const googleNeedsReconnect =
    googleConnected &&
    googleStatus.data !== undefined &&
    "needsReconnect" in googleStatus.data &&
    googleStatus.data.needsReconnect === true;
  const googleLastSyncedAt =
    googleConnected && googleStatus.data !== undefined && "lastSyncedAt" in googleStatus.data
      ? googleStatus.data.lastSyncedAt
      : null;
  const googleLastError =
    googleConnected && googleStatus.data !== undefined && "lastError" in googleStatus.data
      ? googleStatus.data.lastError
      : null;
  const hasActiveFilters =
    filters.search !== null ||
    filters.statuses.length > 0 ||
    filters.clientIds.length > 0 ||
    filters.projectIds.length > 0 ||
    filters.typeIds.length > 0 ||
    filters.assigneeIds.length > 0;

  const firstClientId = filters.clientIds[0];
  const sliceClientName =
    firstClientId === undefined
      ? null
      : extraLabel(
          catalog.clients.find((client) => client.id === firstClientId)?.name ?? "Cliente",
          filters.clientIds.length,
        );
  const myTasksActive =
    me.data?.userId !== undefined &&
    filters.assigneeIds.length === 1 &&
    filters.assigneeIds[0] === me.data.userId;
  const firstAssigneeId = filters.assigneeIds[0];
  const sliceAssigneeLabel =
    firstAssigneeId === undefined
      ? null
      : myTasksActive
        ? "suas tarefas"
        : extraLabel(
            catalog.team.find((member) => member.id === firstAssigneeId)?.name ?? "Responsável",
            filters.assigneeIds.length,
          );
  const sliceTitle = calendarSliceTitle(sliceClientName, sliceAssigneeLabel);

  const nextSliceOccurrence = useMemo(() => {
    const items = sliceScan.data?.occurrences ?? [];
    return [...items]
      .filter((item) => item.status !== "cancelled")
      .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())[0];
  }, [sliceScan.data?.occurrences]);

  const jumpedSliceRef = useRef("");
  const sliceKey = `${filters.clientIds.join(",")}|${filters.assigneeIds.join(",")}`;

  useEffect(() => {
    if (!hasSlice) return;
    if (calendar.isFetching || calendar.isPlaceholderData) return;
    if (visibleOccurrences.length > 0) {
      jumpedSliceRef.current = sliceKey;
      return;
    }
    if (jumpedSliceRef.current === sliceKey) return;
    if (sliceScan.isFetching || sliceScan.isPlaceholderData) return;
    if (nextSliceOccurrence === undefined) {
      jumpedSliceRef.current = sliceKey;
      return;
    }
    const startMs = range.start.getTime();
    const endMs = range.end.getTime();
    const at = nextSliceOccurrence.startsAt.getTime();
    jumpedSliceRef.current = sliceKey;
    if (at >= startMs && at < endMs) return;
    setSearch({ date: localDateKey(nextSliceOccurrence.startsAt, timezone) });
  }, [
    hasSlice,
    sliceKey,
    calendar.isFetching,
    calendar.isPlaceholderData,
    visibleOccurrences.length,
    sliceScan.isFetching,
    sliceScan.isPlaceholderData,
    nextSliceOccurrence,
    range.start,
    range.end,
    setSearch,
  ]);

  const nextJump =
    hasSlice &&
    visibleOccurrences.length === 0 &&
    nextSliceOccurrence !== undefined &&
    (nextSliceOccurrence.startsAt.getTime() < range.start.getTime() ||
      nextSliceOccurrence.startsAt.getTime() >= range.end.getTime())
      ? nextSliceOccurrence
      : undefined;

  const createDefaults = useMemo(() => {
    const projectId = filters.projectIds[0];
    const project =
      projectId === undefined ? undefined : catalog.projects.find((item) => item.id === projectId);
    return {
      clientId: filters.clientIds[0] ?? project?.clientId,
      projectId,
      typeId: filters.typeIds[0],
    };
  }, [catalog.projects, filters.clientIds, filters.projectIds, filters.typeIds]);

  function closeDialog(): void {
    setDialog({ mode: "closed" });
    setSearch({ entrega: undefined });
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden">
      <CalendarToolbar
        view={view}
        focusDate={focusDate}
        timezone={timezone}
        groupBy={groupBy}
        colorBy={colorBy}
        search={search.q ?? ""}
        statuses={filters.statuses}
        clientIds={filters.clientIds}
        clients={catalog.clients}
        connection={connection.state}
        loading={calendar.isFetching}
        resultCount={visibleOccurrences.length}
        googleCount={overlay.length}
        googleConnected={googleConnected}
        googleSyncedAt={googleLastSyncedAt}
        overlaySyncing={googleEvents.isFetching}
        fonte={fonte}
        projectIds={filters.projectIds}
        typeIds={filters.typeIds}
        assigneeIds={filters.assigneeIds}
        projects={catalog.projects}
        types={catalog.types}
        team={catalog.team}
        currentUserId={me.data?.userId}
        onViewChange={(next) => setSearch({ view: next === "mes" ? undefined : next })}
        onNavigate={handleNavigate}
        onGroupByChange={(next) => setSearch({ groupBy: next === "none" ? undefined : next })}
        onColorByChange={(next) => setSearch({ colorBy: next === "type" ? undefined : next })}
        onSearchChange={(q) => setSearch({ q: q === "" ? undefined : q })}
        onStatusesChange={(statuses) => setSearch({ statuses: toCsv(statuses) })}
        onClientIdsChange={(clientIds) => setSearch({ clients: toCsv(clientIds) })}
        onProjectIdsChange={(projectIds) => setSearch({ projects: toCsv(projectIds) })}
        onTypeIdsChange={(typeIds) => setSearch({ types: toCsv(typeIds) })}
        onAssigneeIdsChange={(assigneeIds) => setSearch({ assignees: toCsv(assigneeIds) })}
        onCreate={openCreate}
        onShare={() => {
          if (catalog.clients.length === 0) {
            setNeedClient(true);
            return;
          }
          setSharing(true);
        }}
        onClearFilters={() =>
          setSearch({
            q: undefined,
            statuses: undefined,
            clients: undefined,
            projects: undefined,
            types: undefined,
            assignees: undefined,
            groupBy: undefined,
            fonte: undefined,
          })
        }
        onFonteChange={(next) => setSearch({ fonte: next === "todas" ? undefined : next })}
      />

      {hasSlice && (
        <CalendarSliceBanner
          title={sliceTitle}
          count={visibleOccurrences.length}
          periodPhrase={calendarPeriodPhrase(view)}
          googleHidden
          onClear={() => setSearch({ clients: undefined, assignees: undefined })}
        />
      )}

      {calendar.data?.truncated === true && (
        <div
          role="status"
          className="shrink-0 border-b border-[var(--dream-warning-border)] bg-[var(--dream-warning-bg)] px-4 py-2 text-[0.8125rem] text-[var(--dream-warning-fg)]"
        >
          Este período tem mais entregas do que o limite por consulta. Use os filtros para
          restringir o resultado — o calendário está mostrando apenas parte das entregas.
        </div>
      )}

      {!catalog.isLoading && catalog.clients.length === 0 && <WorkspaceSetupBanner />}

      {googleNeedsReconnect && (
        <div
          role="status"
          className="shrink-0 border-b border-[var(--dream-warning-border)] bg-[var(--dream-warning-bg)] px-4 py-2 text-[0.8125rem] text-[var(--dream-warning-fg)]"
        >
          A Google Agenda precisa de autorização de novo.{" "}
          <Link
            to="/configuracoes"
            search={{ secao: "calendarios" }}
            className="font-semibold underline underline-offset-2"
          >
            Reconecte
          </Link>{" "}
          para as entregas irem para o Google.
        </div>
      )}

      {!googleNeedsReconnect && typeof googleLastError === "string" && googleLastError !== "" && (
        <div
          role="status"
          className="shrink-0 border-b border-[var(--dream-warning-border)] bg-[var(--dream-warning-bg)] px-4 py-2 text-[0.8125rem] text-[var(--dream-warning-fg)]"
        >
          A sync da Agenda falhou: {googleLastError}{" "}
          <Link
            to="/configuracoes"
            search={{ secao: "calendarios" }}
            className="font-semibold underline underline-offset-2"
          >
            Abrir Calendários
          </Link>
        </div>
      )}

      {googleEvents.error !== null && (
        <div
          role="status"
          className="shrink-0 border-b border-[var(--color-line)] bg-[var(--color-surface-subtle)] px-4 py-2 text-[0.8125rem] text-[var(--color-ink-muted)]"
        >
          Não foi possível carregar o overlay da Google Agenda.{" "}
          <button
            type="button"
            className="font-semibold text-[var(--color-ink)] underline underline-offset-2"
            onClick={() => void googleEvents.refetch()}
          >
            Tentar de novo
          </button>
        </div>
      )}

      {!isInitialLoad && catalog.clients.length > 0 && fonte !== "agenda" ? (
        <UpcomingRail
          empty="Nada nesta janela de 14 dias. Crie uma entrega ou mude o filtro de cliente."
          heading="Próximas entregas"
          hideWhenEmpty
          items={upcomingItems}
          onOpen={(deliverableId) => {
            setDialog({
              mode: "edit",
              deliverableId,
              occurrenceStart: new Date().toISOString(),
            });
            setSearch({ entrega: deliverableId });
          }}
          showClient={catalog.clients.length > 1}
          timezone={timezone}
        />
      ) : null}

      {visibleOccurrences.length === 0 &&
      overlay.length === 0 &&
      !isInitialLoad &&
      calendar.error === null ? (
        <CalendarEmptyHint
          readOnly={false}
          filtered={hasActiveFilters || fonte !== "todas"}
          needsSetup={catalog.clients.length === 0}
          fonte={fonte}
          onCreate={openCreate}
          sliceLabel={hasSlice ? sliceTitle : null}
          nextJumpLabel={
            nextJump === undefined ? null : formatSliceJumpLabel(nextJump.startsAt, timezone)
          }
          onGoToNext={
            nextJump === undefined
              ? undefined
              : () => setSearch({ date: localDateKey(nextJump.startsAt, timezone) })
          }
        />
      ) : visibleOccurrences.length === 0 && overlay.length > 0 && !isInitialLoad ? (
        <CalendarEmptyHint readOnly={false} filtered={false} overlayOnly onCreate={openCreate} />
      ) : null}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-[var(--color-surface)]">
        {isInitialLoad ? (
          <CalendarSkeleton />
        ) : calendar.error !== null ? (
          <ErrorState message={calendar.error.message} onRetry={() => void calendar.refetch()} />
        ) : (
          <DreamCalendar
            view={view}
            occurrences={visibleOccurrences}
            types={catalog.types}
            clients={catalog.clients}
            timezone={timezone}
            focusDate={focusDate}
            colorBy={colorBy}
            groupBy={groupBy}
            interactive
            loading={calendar.isFetching && calendar.data === undefined}
            syncing={googleEvents.isFetching}
            onCommand={handleCommand}
            projects={catalog.projects}
            team={catalog.team}
            googleEvents={overlay}
          />
        )}
      </div>

      {dialog.mode !== "closed" && (
        <DeliverableDialog
          state={dialog}
          clients={catalog.clients}
          projects={catalog.projects}
          types={catalog.types}
          team={catalog.team}
          timezone={timezone}
          defaults={createDefaults}
          onClose={closeDialog}
        />
      )}

      {sharing && (
        <ShareAgendaDialog
          clients={catalog.clients}
          projects={catalog.projects}
          initialClientId={filters.clientIds[0]}
          initialProjectId={filters.projectIds[0]}
          onClose={() => setSharing(false)}
        />
      )}

      <NeedClientDialog open={needClient} onOpenChange={setNeedClient} />

      {contextMenu !== null && (
        <EventContextMenu
          state={contextMenu}
          onClose={() => setContextMenu(null)}
          onOpen={() => {
            const current = contextMenu;
            setContextMenu(null);
            setDialog({
              mode: "edit",
              deliverableId: current.deliverableId,
              occurrenceStart: current.occurrenceStart,
            });
            setSearch({ entrega: current.deliverableId });
            pushRecentDeliverable({ id: current.deliverableId, title: current.title });
          }}
          onDuplicate={() => {
            const id = contextMenu.deliverableId;
            setContextMenu(null);
            duplicateMutation.mutate(id);
          }}
          onDelete={() => {
            const id = contextMenu.deliverableId;
            setContextMenu(null);
            deleteFromMenu.mutate({ id, version: contextVersion });
          }}
          onCopy={() => {
            void navigator.clipboard.writeText(contextMenu.title);
            toast.success("Título copiado");
            setContextMenu(null);
          }}
          onColor={(color) => {
            if (contextMenu.typeId === "") return;
            setPrefs({
              typeColorOverrides: { ...prefs.typeColorOverrides, [contextMenu.typeId]: color },
            });
            setContextMenu(null);
          }}
          onReschedule={(shift) => {
            const current = contextMenu;
            setContextMenu(null);
            const start = DateTime.fromISO(current.startsAt);
            const end = DateTime.fromISO(current.endsAt);
            const delta =
              shift === "hour" ? { hours: 1 } : shift === "tomorrow" ? { days: 1 } : { weeks: 1 };
            moveMutation.mutate(
              {
                deliverableId: current.deliverableId,
                version: contextVersion,
                startsAt: start.plus(delta).toUTC().toISO() ?? current.startsAt,
                endsAt: end.plus(delta).toUTC().toISO() ?? current.endsAt,
                occurrenceStart: current.occurrenceStart,
                isAllDay: current.allDay,
              },
              {
                onSuccess: () => toast.success("Entrega reagendada"),
                onError: (error) => reportFailure(error, "reagendar"),
              },
            );
          }}
        />
      )}
    </div>
  );
}

/** Chaves estáveis para os placeholders, em vez do índice do array. */
const WEEKDAY_KEYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sab"] as const;
const SKELETON_CELL_KEYS = Array.from({ length: 35 }, (_, i) => `celula-${i}`);

function CalendarSkeleton() {
  return (
    <div className="p-4" role="status" aria-busy="true" aria-label="Carregando calendário">
      <div className="mb-2 grid grid-cols-7 gap-2">
        {WEEKDAY_KEYS.map((key) => (
          <Skeleton key={key} className="h-4" />
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {SKELETON_CELL_KEYS.map((key) => (
          <Skeleton key={key} className="h-24" />
        ))}
      </div>
    </div>
  );
}
